import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import security from '@/common/security'
import store from '@/store'

const module = 'events/eventProfilesModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    channel: 'client',
    enabled: true,
    created_at: '',
    updated_at: '',
    partner: { id: null, name: null },
    agency: { id: null, name: null },
    client: { id: null, name: null },
    event_profile_events: [],
    clone_option: 'client-to-client',
    clone_step: 1,
    show_form: false,
    can_continue: true,
    source_client_id: null,
    source_client_name: null,
    dest_client_id: null,
    dest_client_name: null,
    source_profile_id: null,
    source_profile_name: null,
    forwarding_users: [],
    assign_units: [],
    assign_event_pool_id: null,
    create_event_pool: true,
    event_pool_name: null,
    event_pool_option: 'none', // 'none', 'assign', 'create'
    cloning_state: 'show-wizard', // 'show-wizard', 'in-progress', 'done', 'failed'
    deleted: false
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

/**
 * Returns a copy of the default form.
 *
 * @return Object
 */
function formDefaults () {
  return {
    event_codes: [],
    event_sounds: [],
    forwarding_defaults: []
  }
}

function transformEventPoolsClient (pools) {
  return pools.map(pool => pool.id)
}

function transformEventPoolsMonitored (monitored_pools) {
  const def_set_by_user_id = security.user().id
  const def_set_by_user_full_name = security.user().full_name
  return monitored_pools.map(monitored_pool => {
    let set_by_user_id
    if (!monitored_pool.hasOwnProperty('set_by_user_id') || monitored_pool.set_by_user_id === null) {
      set_by_user_id = def_set_by_user_id
    } else {
      set_by_user_id = monitored_pool.set_by_user_id
    }

    let set_by_user_full_name
    if (!monitored_pool.hasOwnProperty('set_by_user_full_name') || monitored_pool.set_by_user_full_name === null) {
      set_by_user_full_name = def_set_by_user_full_name
    } else {
      set_by_user_full_name = monitored_pool.set_by_user_full_name
    }

    return {
      id: monitored_pool.id,
      name: monitored_pool.name,
      set_by_user_id: set_by_user_id,
      set_by_user_full_name: set_by_user_full_name
    }
  })
}

function transformProfileEvents (channel, event_profile_events) {
  let results
  if (channel === 'monitored') {
    results = event_profile_events.map((event) => {
      event.monitored_pools = transformEventPoolsMonitored(event.monitored_pools)
      return event
    })
  } else {
    results = event_profile_events.map((event) => {
      event.pools = transformEventPoolsClient(event.pools)
      return event
    })
  }

  return results
}

function transformCreate (data) {
  return {
    name: data.name,
    channel: data.channel,
    enabled: data.enabled,
    partner_id: data.partner.id,
    agency_id: data.agency.id,
    client_id: data.client.id,
    event_profile_events: transformProfileEvents(data.channel, data.event_profile_events)
  }
}

function transformUpdate (data) {
  return {
    id: data.id,
    name: data.name,
    channel: data.channel,
    enabled: data.enabled,
    partner_id: data.partner.id,
    agency_id: data.agency.id,
    client_id: data.client.id
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  isModified: false,
  isLoading: false,
  search_mode: 'client-profile'
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  getSearchMode (state) {
    return state.search_mode
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`events/profiles/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    context.commit('reset')
    return ApiService
      .get('events/profiles/create')
      .then((response) => {
        context.commit('setEventCodes', response.data.data)
        context.commit('setEventAlarms', response.data.data.event_sounds)
        context.commit('setForwardingDefaults', response.data.data.forwarding_defaults)
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`events/profiles/${id}`)
      .then((response) => {
        const data = response.data.data.event_profile
        context.commit('setEventCodes', response.data.data)
        context.commit('set', data)
        context.commit('setIsModified', false)
        context.commit('setEventAlarms', response.data.data.event_sounds)
        context.commit('setForwardingDefaults', response.data.data.forwarding_defaults)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    return ApiService
      .post(`events/profiles`, transformCreate(context.getters['get']))
      .then((response) => {
        context.commit('clearName')
        context.commit('clearEvents')
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * Post (create) new event profile event through the API.
   *
   * @param context
   * @param data
   * @param params
   * @returns {*}
   */
  postEventProfileEvent (context, { data, params }) {
    const channel = context.getters['get'].channel
    if (channel === 'monitored') {
      data.monitored_pools = transformEventPoolsMonitored(data.monitored_pools)
    } else {
      // Transform pool objects into ids
      data.pools = transformEventPoolsClient(data.pools)
    }

    const id = context.getters['get'].id
    return ApiService
      .post(`events/profiles/${id}/profile-event`, { data: data, params: {} })
      .then((response) => {
        const data = response.data.data
        context.commit('set', data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Put (update) existing event profile event
   * @param context
   * @param data
   * @param params
   * @returns {*}
   */
  putEventProfileEvent (context, { data, params }) {
    let profile_id = context.getters['get'].id
    let event_id = context.getters['get'].event_profile_events[params.index].id

    const channel = context.getters['get'].channel
    if (channel === 'monitored') {
      data.monitored_pools = transformEventPoolsMonitored(data.monitored_pools)
    } else {
      // Transform pool objects into ids
      data.pools = transformEventPoolsClient(data.pools)
    }

    return ApiService
      .put(`events/profiles/${profile_id}/profile-event/${event_id}`, { data: data, params: {} })
      .then((response) => {
        const data = response.data.data
        context.commit('set', data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let id = context.getters['get'].id
    return ApiService
      .put(`events/profiles/${id}`, transformUpdate(context.getters['get']))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`events/profiles/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        return response
      })
  },

  /**
   * DELETE existing event profile event through the API.
   *
   * @param context
   * @param index
   * @returns {*}
   */
  deleteEvent (context, index) {
    let profile_id = context.getters['get'].id
    let event_id = context.getters['get'].event_profile_events[index].id

    return ApiService
      .delete(`events/profiles/${profile_id}/profile-event/${event_id}`)
      .then((response) => {
        const data = response.data.data
        context.commit('set', data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`events/profiles`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple monitored event profile records from the API.
   *
   * @param context
   * @param params
   * @returns {*}
   */
  queryMonitoredChannel (context, params) {
    return ApiService
      .get(`events/profiles/monitored`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple client event profile records from the API.
   *
   * @param context
   * @param params
   * @returns {*}
   */
  queryClientChannel (context, params) {
    return ApiService
      .get(`events/profiles/client`, params)
      .then((response) => {
        return response
      })
  },

  postCloneClientProfile (context) {
    const fields = context.getters['get']

    const data = {
      source_event_profile_id: fields.source_profile_id,
      destination_client_id: fields.dest_client_id,
      safe_zone_forward_user_ids: fields.forwarding_users,
      assign_unit_ids: fields.assign_units,
      create_new_event_pool: fields.create_event_pool,
      event_pool_name: fields.event_pool_name,
      event_pool_description: null,
      use_event_pool_id: fields.assign_event_pool_id,
      event_profile_name: fields.name
    }

    return ApiService
      .post(`events/profiles/client/clone`, { data: data, params: {} })
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  clearName (state) {
    state.data.name = ''
  },

  clearEvents (state) {
    state.data.event_profile_events = []
  },

  setEventCodes (state, data) {
    state.form.event_codes = Object.assign({}, data.event_codes)

    if (data.hasOwnProperty('event_profile')) {
      data.event_profile.event_profile_events.forEach(event => {
        if (!state.form.event_codes.hasOwnProperty(event.event_code)) {
          state.form.event_codes[event.event_code] = event.message
        }
      })
    }
  },

  addEventCodeToForm (state, { event_code, message }) {
    state.form.event_codes[event_code] = message
  },

  addEventCodeMsgToForm (state, {event_code, message}) {
    state.form.event_codes[event_code] = message
  },

  setEventAlarms (state, data) {
    state.form.event_sounds = Object.assign({}, data)
  },

  setForwardingDefaults (state, data) {
    state.form.forwarding_defaults = Object.assign({}, data)
  },

  setSearchMode (state, search_mode) {
    state.search_mode = search_mode
  },

  setClient (state, client) {
    store.commit('events/eventProfilesModule/clearClient')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state       Object
   * @param event_index Number Index of event
   * @param index       Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceForwarding (state, { event_index, index, replacement }) {
    state.isModified = true
    state.data.event_profile_events[event_index].forwarding.splice(index, 1, replacement)
  },

  pushForwarding (state, {event_index, data}) {
    state.isModified = true
    state.data.event_profile_events[event_index].forwarding.push(data)
  },

  pullForwarding (state, {event_index, index}) {
    state.isModified = true
    utils.pullProp(state.data.event_profile_events[event_index], 'forwarding', index)
  },

  spliceDataField (state, { event_index, index, replacement }) {
    state.isModified = true
    state.data.event_profile_events[event_index].data.splice(index, 1, replacement)
  },

  pushDataField (state, {event_index, data}) {
    state.isModified = true
    state.data.event_profile_events[event_index].data.push(data)
  },

  pullDataField (state, {event_index, index}) {
    state.isModified = true
    utils.pullProp(state.data.event_profile_events[event_index], 'data', index)
  },

  pullEvent (state, { index }) {
    state.isModified = true
    utils.pullProp(state.data, 'event_profile_events', index)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.form = Object.assign({}, formDefaults())
    state.isModified = false
  },

  resetEventPools (state) {
    state.data.event_profile_events.forEach((event) => {
      event.pools = []
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/events/profiles/eventProfilesModule.js